import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import * as ozon from "@/api/ozonSupply";
import { extractErrorMessage } from "@/lib/api-errors";
import type { Selection } from "./PlacesTable";
import { withPick } from "./PlacesTable";
import type { LayoutTrouble, PlacesInfo } from "./ozonStage";
import { REGISTRY, dirKey, placesKey } from "./ozonQueries";
import {
  useOzonDocActions,
  useOzonPlaceEdits,
  type DocActions,
  type PlaceEditActions,
} from "./useOzonDocActions";

/** Всё, что страница поставки знает про раскладку: сама раскладка, чем её правят и что с ней
 *  делают наружу.
 *
 *  ⚠️ Живёт НА СТРАНИЦЕ, а не внутри таблицы мест: главная кнопка стадии («Разложить»,
 *  «Отправить места», «Записать в 1С») стоит в шапке, над разделами, и дёргает ровно те же
 *  мутации, что и раздел раскладки. Разведи их по двум местам — и «занято» у кнопки было бы
 *  своё, а у таблицы своё.
 */
export interface OzonLayout {
  data: ozon.OzonPlaces | undefined;
  places: ozon.SavedPlace[];
  /** непустые места: только они едут на площадку и получают коды */
  sendable: ozon.SavedPlace[];
  /** сколько непустых мест уже с кодом */
  coded: number;
  /** раскладка в двух словах для `actionsFor`; `null` — её ещё не спрашивали ИЛИ не прочитали */
  info: PlacesInfo | null;
  /** ⚠️ Раскладка не прочиталась — текст ошибки запроса, `null` — всё в порядке.
   *
   *  Наружу именно ошибкой, а не пустым списком мест: пустой список экран показывал как «Мест
   *  ещё нет — нажмите „Разложить“» про поставку, места которой, возможно, уже стоят на
   *  площадке, а `actionsFor` молча терял печать и сборочный лист. */
  error: string | null;
  /** чек-лист площадки не прочитался — текст ошибки запроса; `null` — всё в порядке */
  rulesError: string | null;
  /** обе ошибки одним объектом — ровно то, что берёт `actionsFor` */
  trouble: LayoutTrouble;
  strategy: string;
  setStrategy: (v: string) => void;
  edits: PlaceEditActions;
  docs: DocActions;
  /** Положить свежую раскладку, как это делают сами мутации: перечитать направление и снять
   *  выбор. Наружу — ради `change/abort`: он отвечает ВОССТАНОВЛЕННОЙ раскладкой, и класть её
   *  вторым способом значило бы забыть половину того, что делает правка. */
  put: (d: ozon.OzonPlaces) => void;
  /** Перечитать НАПРАВЛЕНИЕ и список: по направлению считаются стадия, шапка, главная кнопка и
   *  сама запись правки. Наружу — ради правки состава: её ручки меняют то же самое. */
  afterLayoutChange: () => void;
  /** Когда раскладку в последний раз меняли (`dataUpdatedAt`). Им помечается превью правки:
   *  план — функция от текущей раскладки, и без метки он остался бы от прошлой правки. */
  stamp: number;
  rules: ozon.OzonRule[];
  /** выбор в таблице мест и то, что от него зависит */
  sel: Selection;
  /** номера раскрытых паллет */
  open: number[];
  toggleOpen: (pos: number) => void;
  togglePick: (pick: ozon.OzonPick) => void;
  fetching: boolean;
  busy: boolean;
}

export function useOzonLayout(
  shipmentId: number,
  /** Номер поставки: без него грузоместа отправлять некуда — заявки ещё нет. */
  supplyNumber: string | null,
  /** Спрашивать ли раскладку: до заявки грузомест не существует, и ручка ходила бы впустую. */
  enabled: boolean,
): OzonLayout {
  const qc = useQueryClient();
  const key = placesKey(shipmentId);
  const q = useQuery({
    queryKey: key,
    queryFn: () => ozon.getOzonPlaces(shipmentId),
    enabled,
    staleTime: 30_000,
  });
  const [strategy, setStrategy] = useState("");
  const [picked, setPicked] = useState<ozon.OzonPick[]>([]);
  const [open, setOpen] = useState<number[]>([]);
  const [dest, setDest] = useState<number | null>(null); // назначение сборки; null — новая
  const [parting, setParting] = useState(false); // открыт перенос части количества

  /** Раскладка, коды или документы 1С изменились — перечитать НАПРАВЛЕНИЕ.
   *
   *  ⚠️ Одна точка на все мутации мест, отправки, перевыпуска, снятия кодов и цикла 1С. Стадия
   *  (`stageOf`), шапка, главная кнопка и вкладки считаются по направлению, а не по раскладке:
   *  обнови мутация только `placesKey` — после «Отправить места» главная кнопка так и осталась
   *  бы «Отправить места», а второй клик отправил бы их ещё раз. Список фиксации обновляем той
   *  же рукой: та же поставка стоит там строкой.
   */
  const afterLayoutChange = (): void => {
    void qc.invalidateQueries({ queryKey: dirKey(shipmentId) });
    void qc.invalidateQueries({ queryKey: REGISTRY });
  };

  /** Снять выбор и раскрытия: после правки номера мест другие, и выбор по старым номерам означал
   *  бы не те места — паллета, с которой работали, схлопывалась, а посторонняя открывалась сама. */
  const resetSel = (): void => {
    setPicked([]);
    setOpen([]);
    setDest(null);
    setParting(false);
  };

  const put = (d: ozon.OzonPlaces): void => {
    qc.setQueryData(key, d);
    afterLayoutChange();
    resetSel();
  };
  // чек-лист спрашиваем у площадки, а не выводим сами: она знает про свои требования больше
  const rulesQ = useQuery({
    queryKey: ["supply", "ozon", "checklist", shipmentId],
    queryFn: () => ozon.getOzonChecklist(shipmentId),
    enabled: enabled && supplyNumber !== null,
    staleTime: 60_000,
  });
  const refetchRules = (): void => {
    void rulesQ.refetch();
  };
  const edits = useOzonPlaceEdits(shipmentId, strategy, {
    put,
    // ⚠️ тем же путём, что `put`: смена режима удаляет ВСЕ места на сервере, и оставленный выбор
    // рисовал «Выбрано: 1 позиция» над пустой таблицей
    refetch: () => {
      void q.refetch();
      resetSel();
    },
    refetchRules,
    afterLayoutChange,
  });
  const docs = useOzonDocActions(shipmentId, supplyNumber, {
    put,
    refetchRules,
    afterLayoutChange,
  });

  // ⚠️ Ошибка запроса — наружу текстом, а не пустым списком: раздел рисует её вместо таблицы, а
  // `actionsFor` гасит с причиной кнопки, которым нужна раскладка
  const placesError = q.isError
    ? extractErrorMessage(q.error, "Раскладка не прочиталась")
    : null;
  const rulesError = rulesQ.isError
    ? extractErrorMessage(rulesQ.error, "Чек-лист площадки не прочитался")
    : null;
  const places = q.data?.places ?? [];
  // ⚠️ Считаем только НЕПУСТЫЕ места: пустое на площадку не едет (`cargoes.send` их отбрасывает) и
  // кода не получит никогда. Пока счёт шёл по всем, одно забытое пустое место навсегда прятало
  // «Перевыпустить места» и показывало «Дослать 1», которую нечего досылать
  const sendable = places.filter((p) => p.items.length > 0);
  const coded = sendable.filter((p) => p.cargo_id).length;

  return {
    data: q.data,
    places,
    sendable,
    coded,
    // ⚠️ `null`, пока раскладки нет на руках: набор действий, посчитанный по пустому месту, обещал
    // бы кнопки, которым нечего печатать и нечего отправлять
    error: placesError,
    rulesError,
    trouble: { places: placesError, rules: rulesError },
    info: q.data
      ? {
          total: places.length,
          sendable: sendable.length,
          coded,
          pallets: places.some((p) => p.transport_cargo_id),
        }
      : null,
    strategy,
    setStrategy,
    edits,
    docs,
    put,
    afterLayoutChange,
    stamp: q.dataUpdatedAt,
    rules: rulesQ.data ?? [],
    sel: { picked, setPicked, dest, setDest, parting, setParting },
    open,
    toggleOpen: (pos) =>
      setOpen((v) =>
        v.includes(pos) ? v.filter((p) => p !== pos) : [...v, pos],
      ),
    togglePick: (pick) => {
      setPicked(withPick(picked, pick));
      setParting(false);
    },
    fetching: q.isFetching,
    busy: edits.busy || docs.busy,
  };
}
